import { useTranslation } from "react-i18next";
import {
  Activity,
  Award,
  Bot,
  ChartColumn,
  CirclePlay,
  Clock,
  DatabaseBackup,
  DatabaseZap,
  Gauge,
  GraduationCap,
  KeyRound,
  Lightbulb,
  Lock,
  MapPinned,
  MonitorPlay,
  Network,
  Newspaper,
  RadioTower,
  SatelliteDish,
  ScanText,
  School,
  Server,
  ShieldCheck,
  Ship,
  Stethoscope,
  Trophy,
  Users,
  UsersRound,
  Wifi,
  Workflow,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { MenuGroup } from "../../components/Header/HeaderMenu";
import inepLogo from "../../assets/logos/inep.webp";
import mecLogo from "../../assets/logos/mec.png";
import rnpLogo from "../../assets/logos/rnp-logo-recortado.png";
import ciscoLogo from "../../assets/logos/cisco.png";
import sedapImg from "../../assets/plataformas/sedap.png";
import viaipeImg from "../../assets/plataformas/viaipe.png";
import senai5gImg from "../../assets/plataformas/senai5g.png";
import barcoImg from "../../assets/plataformas/barco-amazonas.png";

// Textos em cases_page.* nos locales (pt é o idioma de fallback). Aqui só a estrutura: ids, ícones, cores e imagens.

// Fotos e logos que não vêm no bundle: ficam em public/case-assets/. Se o arquivo faltar, a página mostra um
// placeholder no lugar (ver CaseImage).
// âncoras da página que não vêm de um id de case
export const FEATURED_ID = "destaque";
export const MEDIA_ID = "calriz-na-midia";

const asset = (path: string) => `${process.env.PUBLIC_URL}/case-assets/${path}`;

export type Logo = { src: string; alt: string; key: string };
export type Result = { value: string; label: string };

export type Featured = {
  Icon: LucideIcon;
  title: string;
  text: string;
  logo: Logo;
  stats: (Result & { accent: boolean })[];
  techs: string[];
  itemsLabel: string;
  items: { Icon: LucideIcon; title: string; text: string }[];
};

export type Story = {
  id: string;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  context: string;
  challenge: string;
  challengeNote?: string;
  done: string[];
  results: Result[];
  techs: string[];
  logos: Logo[];
  /** Logos pequenos dos parceiros na linha de contexto do card do desafio ("MEC + RNP · ..."). */
  partners: Logo[];
  link?: { label: string; href: string };
};

// date em ISO (yyyy-mm-dd), formatada no idioma da página
export type MediaItem = {
  key: string;
  image: string;
  source: string;
  date: string;
  href: string;
  name: string;
  headline: string;
  text: string;
  imgAlt: string;
};

// highlight = card em destaque (fundo em gradiente claro da marca)
export type CaseCard = { key: string; title: string; tag?: string; bullets: string[]; highlight: boolean; Icon: LucideIcon; to?: string };
export type SecurityItem = { key: string; Icon: LucideIcon; title: string; text: string };

export type CaseGroup = {
  id: string;
  Icon: LucideIcon;
  layout: "cards" | "split";
  title: string;
  text: string;
  cards: CaseCard[];
  // só no layout "split" (Moodle, rede e segurança)
  leftLabel?: string;
  rightLabel?: string;
  items?: SecurityItem[];
};

type CardConfig = { key: string; Icon: LucideIcon; highlight?: boolean; to?: string };

const FEATURED_ICONS = [Users, GraduationCap, ChartColumn, ShieldCheck, Clock, Lightbulb];

const RNP: Logo = { key: "rnp", src: rnpLogo, alt: "RNP" };

// Icon = selo ao lado do título do case
const STORIES_CONFIG: { id: string; key: string; Icon: LucideIcon; logos?: Logo[]; partners?: Logo[] }[] = [
  { id: "nordeste-conectado", key: "nordeste", Icon: RadioTower, logos: [{ key: "cisco", src: ciscoLogo, alt: "Cisco" }] },
  {
    id: "sedap",
    key: "sedap",
    Icon: DatabaseZap,
    logos: [{ key: "inep", src: inepLogo, alt: "INEP" }, { key: "mec", src: mecLogo, alt: "MEC" }, RNP],
  },
  { id: "fmu", key: "fmu", Icon: MonitorPlay, logos: [{ key: "fmu", src: asset("logos/fmu.png"), alt: "FMU" }] },
];

// matérias sobre os projetos: print do topo da página, veículo, data de publicação e link para a original
const MEDIA_CONFIG: { key: string; image: string; source: string; date: string; href: string }[] = [
  {
    key: "sedap",
    image: sedapImg,
    source: "Inep · gov.br",
    date: "2022-03-03",
    href: "https://www.gov.br/inep/pt-br/areas-de-atuacao/gestao-do-conhecimento-e-estudos-educacionais/cgdi/servico-de-acesso-a-dados-protegidos-sedap",
  },
  {
    key: "viaipe",
    image: viaipeImg,
    source: "RNP",
    date: "2016-06-28",
    href: "https://www.rnp.br/2016/06/28/viaip-modelo-em-transparncia-na-qualidade-da-conexo/",
  },
  {
    key: "senai5g",
    image: senai5gImg,
    source: "SENAI-SP",
    date: "2022-12-22",
    href: "https://www.sp.senai.br/noticia/escolas-moveis-do-senai-sp-percorrem-o-estado-com-formacao-profissional-e-novas-tecnologias-em-2022-2",
  },
  {
    key: "barco",
    image: barcoImg,
    source: "Portal do Comércio · CNC",
    date: "2024-06-03",
    href: "https://portaldocomercio.org.br/sistema-comercio/academia-fluvial-senac-e-cisco-levam-cursos-para-o-interior-do-amazonas/",
  },
];

// Icon do grupo = selo na coluna do título; Icon do card = selo do case
const GROUPS_CONFIG: {
  id: string;
  key: string;
  Icon: LucideIcon;
  layout: CaseGroup["layout"];
  cards: CardConfig[];
  items?: { key: string; Icon: LucideIcon }[];
}[] = [
  {
    id: "educacao-e-nuvem",
    key: "educacao",
    Icon: GraduationCap,
    layout: "cards",
    cards: [
      { key: "guaratingueta", Icon: School },
      { key: "bertioga", Icon: Award, highlight: true },
      { key: "aluno_tutor", Icon: UsersRound },
      { key: "eduplay", Icon: CirclePlay },
      { key: "hackers_do_bem", Icon: Gauge },
    ],
  },
  {
    id: "moodle-rede-seguranca",
    key: "moodle",
    Icon: ShieldCheck,
    layout: "split",
    cards: [
      { key: "fmu", Icon: MonitorPlay, highlight: true, to: "#fmu" },
      { key: "conecta_rede", Icon: Network },
    ],
    items: [
      { key: "inep", Icon: Lock },
      { key: "disaster_recovery", Icon: DatabaseBackup },
      { key: "secops", Icon: ShieldCheck },
    ],
  },
  {
    id: "plataformas-e-dados",
    key: "dados",
    Icon: ChartColumn,
    layout: "cards",
    cards: [
      { key: "inep", Icon: DatabaseZap, highlight: true, to: "#sedap" },
      { key: "pucpr", Icon: Stethoscope },
      { key: "cni", Icon: Bot },
      { key: "insight_cities", Icon: MapPinned },
      { key: "viaipe", Icon: Activity },
      { key: "renova", Icon: ScanText },
    ],
  },
  {
    id: "integracoes-e-infraestrutura",
    key: "integracoes",
    Icon: Workflow,
    layout: "cards",
    cards: [
      { key: "seduc_to", Icon: KeyRound },
      { key: "workspace_rnp", Icon: Users, highlight: true },
      { key: "sao_caetano", Icon: Server },
    ],
  },
  {
    id: "conectividade-em-campo",
    key: "conectividade",
    Icon: RadioTower,
    layout: "cards",
    cards: [
      { key: "nordeste", Icon: RadioTower, highlight: true, to: "#nordeste-conectado" },
      { key: "maringa", Icon: Wifi },
      { key: "balsa_escola", Icon: Ship },
      { key: "senac_5g", Icon: SatelliteDish },
    ],
  },
];

const list = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);

export function useCases() {
  const { t } = useTranslation();
  const results = (key: string) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? (value as Result[]) : [];
  };

  const featuredItems = t("cases_page.featured.items", { returnObjects: true });
  const featured: Featured = {
    Icon: School,
    title: t("cases_page.featured.title"),
    text: t("cases_page.featured.text"),
    logo: { key: "goias", src: asset("logos/goias.png"), alt: t("cases_page.featured.logo_alt") },
    // o último número (meta de automação) vai em rosa, como no material original
    stats: results("cases_page.featured.stats").map((s, i, all) => ({ ...s, accent: i === all.length - 1 })),
    // vem como texto corrido nos locales; vira chips como nos outros cases
    techs: list(t("cases_page.featured.tech").split(",").map((tech: string) => tech.trim()).filter(Boolean)),
    itemsLabel: t("cases_page.featured.items_label"),
    items: (Array.isArray(featuredItems) ? (featuredItems as { title: string; text: string }[]) : []).map((item, i) => ({
      ...item,
      Icon: FEATURED_ICONS[i % FEATURED_ICONS.length],
    })),
  };

  const stories: Story[] = STORIES_CONFIG.map(({ id, key, Icon, logos = [], partners = [] }) => {
    const k = `cases_page.stories.${key}`;
    const note = t(`${k}.challenge_note`, "");
    const href = t(`${k}.link_href`, "");
    return {
      id,
      Icon,
      title: t(`${k}.title`),
      subtitle: t(`${k}.subtitle`),
      context: t(`${k}.context`),
      challenge: t(`${k}.challenge`),
      challengeNote: note || undefined,
      done: list(t(`${k}.done`, { returnObjects: true })),
      results: results(`${k}.results`),
      techs: list(t(`${k}.techs`, { returnObjects: true })),
      logos,
      partners,
      link: href ? { label: t(`${k}.link_label`), href } : undefined,
    };
  });

  const media: MediaItem[] = MEDIA_CONFIG.map((item) => ({
    ...item,
    name: t(`cases_page.media.items.${item.key}.name`),
    headline: t(`cases_page.media.items.${item.key}.headline`),
    text: t(`cases_page.media.items.${item.key}.text`),
    imgAlt: t(`cases_page.media.items.${item.key}.img_alt`),
  }));

  const groups: CaseGroup[] = GROUPS_CONFIG.map(({ id, key, Icon: GroupIcon, layout, cards, items }) => {
    const k = `cases_page.groups.${key}`;
    return {
      id,
      Icon: GroupIcon,
      layout,
      title: t(`${k}.title`),
      text: t(`${k}.text`),
      cards: cards.map(({ key: card, highlight = false, Icon, to }) => {
        const tag = t(`${k}.cards.${card}.tag`, "");
        return {
          key: card,
          highlight,
          Icon,
          to,
          title: t(`${k}.cards.${card}.title`),
          tag: tag || undefined,
          bullets: list(t(`${k}.cards.${card}.bullets`, { returnObjects: true })),
        };
      }),
      leftLabel: layout === "split" ? t(`${k}.left_label`) : undefined,
      rightLabel: layout === "split" ? t(`${k}.right_label`) : undefined,
      items: items?.map(({ key: item, Icon }) => ({
        key: item,
        Icon,
        title: t(`${k}.items.${item}.title`),
        text: t(`${k}.items.${item}.text`),
      })),
    };
  });

  return { featured, stories, media, groups };
}

// Menu "Cases" do header: os cases em destaque, as matérias na mídia e um tópico por área, na ordem da página.
// Cada item leva à âncora dele na página (o case em detalhe, quando o card aponta para um) ou à seção.
export function useCaseMenuGroups(): MenuGroup[] {
  const { t } = useTranslation();
  const anchor = (id: string) => `/cases#${id}`;

  const featured: MenuGroup = {
    key: FEATURED_ID,
    title: t("header.cases_menu.featured", "Cases em destaque"),
    icon: Trophy,
    to: anchor(FEATURED_ID),
    items: [
      { key: FEATURED_ID, label: t("cases_page.featured.title"), to: anchor(FEATURED_ID) },
      ...STORIES_CONFIG.map(({ id, key }) => ({ key: id, label: t(`cases_page.stories.${key}.title`), to: anchor(id) })),
    ],
  };

  const media: MenuGroup = {
    key: MEDIA_ID,
    title: t("cases_page.media.title"),
    icon: Newspaper,
    to: anchor(MEDIA_ID),
    items: MEDIA_CONFIG.map(({ key }) => ({
      key,
      label: t(`cases_page.media.items.${key}.name`),
      to: anchor(MEDIA_ID),
    })),
  };

  const areas: MenuGroup[] = GROUPS_CONFIG.map(({ id, key, Icon, cards, items = [] }) => {
    const k = `cases_page.groups.${key}`;
    return {
      key: id,
      title: t(`${k}.title`),
      icon: Icon,
      to: anchor(id),
      items: [
        ...cards.map(({ key: card, to }) => ({
          key: card,
          label: t(`${k}.cards.${card}.title`),
          to: to ? `/cases${to}` : anchor(id),
        })),
        ...items.map(({ key: item }) => ({ key: item, label: t(`${k}.items.${item}.title`), to: anchor(id) })),
      ],
    };
  });

  return [featured, media, ...areas];
}
